import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './components/home/home.component';
import { PageNotFoundComponent } from './components/layout/not-found404/not-found404.component';
import { LoginComponent } from './components/login/login.component';
import { NoRoleComponent } from './components/no-role/no-role.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ScheduleTasksComponent } from './components/schedule/schedule-tasks/schedule-tasks.component';
import { SupplierProfileComponent } from './components/supplier/supplier-profile/supplier-profile.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { WorkloadComponent } from './components/workload/workload/workload.component';
import { AuthenticationGuard } from './core/guards/auth-guard';
import { PermissionGuard } from './core/guards/pemission-guard';
import { ProductListComponent } from './components/products/product-list/product-list.component';
import { SupplierListComponent } from './components/supplier/supplier-list/supplier-list.component';
import { ServiceListComponent } from './components/services/service-list/service-list.component';
import { AdvertisementComponent } from './components/advertisement/advertisement.component';
import { TermsComponent } from './components/terms/terms.component';
import { PrivacyComponent } from './components/privacy/privacy.component';
import { ScheduleTasksCRMListComponent } from './components/schedule/schedule-tasks-crm-list/schedule-tasks-crm-list.component';
import { SapArticlesComponent } from './components/sap-articles/sap-articles.component';
import { ManufacturingCalendarComponent } from './components/manufacturing-calendar/manufacturing-calendar.component';
import { BudgetComponent } from './components/budget/budget.component';
import { InterestRateComponent } from './components/budget/interest-rate/interest-rate.component';
import { OptionalAccessoryComponent } from './components/budget/optional-accessory/optional-accessory.component';
import { BudgetListComponent } from './components/budget/budget-list/budget-list.component';
import { SupplierPaymentComponent } from './components/supplier/supplier-payment/supplier-payment.component';
import { FAQsComponent } from './components/faqs/faqs.component';
import { ScoreComponent } from './components/score/score.component';
import { CertificationsComponent } from './components/certifications/certifications.component';
import { NotificationsCenterComponent } from './components/notifications/notifications-center/notifications-center.component';
import { LevelComponent } from './components/level/level.component';
import { FactorComponent } from './components/factor/factor.component';
import { LevelBenefitsComponent } from './components/level/level-benefits/level-benefits.component';
import { FranchiseComponent } from './components/franchise/franchise.component';
import { PointSaleListComponent } from './components/franchise/point-sale-list/point-sale-list.component';
import { FranchiseZoneComponent } from './components/franchise/franchise-zone/franchise-zone.component';
import { FranchiseFilesUploaderComponent } from './components/franchise-files-uploader/franchise-files-uploader.component';
import { ReportingTabsComponent } from './components/reports/reporting-tabs/reporting-tabs.component';
import { ServiceRequestComponent } from './components/service-request/service-request.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'welcome',
    pathMatch: 'full',
    title: 'Home',
  },
  {
    path: 'tp-terms',
    component: TermsComponent,
    title: 'Términos y condiciones',
  },
  {
    path: 'service-request',
    component: ServiceRequestComponent,
    title: 'Solicitud de servicio',
  },
  {
    path: 'privacy',
    component: PrivacyComponent,
    title: 'Política de privacidad',
  },
  {
    path: 'login',
    component: LoginComponent,
    title: 'Login',
  },
  {
    path: 'no-role',
    component: NoRoleComponent,
    title: 'Sin rol',
  },
  {
    path: 'welcome',
    component: WelcomeComponent,
    canActivate: [AuthenticationGuard],
    title: 'Bienvenida',
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [AuthenticationGuard],
    title: 'Perfil',
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./components/dashboard/dashboard.component').then(
        (c) => c.DashboardComponent
      ),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['view:dashboard'],
      breadcrumb: { level: 1, description: 'Dashboard general' },
      group: 'dashboard',
      menu: { description: 'Dashboard' },
    },
    title: 'Dashboard',
  },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:contractorStatus'],
      breadcrumb: { level: 1, description: 'Estado de prestadores' },
      group: 'contractor',
      menu: { description: 'Estado' },
    },
    title: 'Estado de prestadores',
  },
  {
    path: 'notifications-center',
    component: NotificationsCenterComponent,
    canActivate: [AuthenticationGuard],
    data: {
      group: 'notifications',
      breadcrumb: { level: 1, description: 'Centro de notificaciones' },
      menu: { description: 'Centro de notificaciones' },
    },
    title: 'Centro de notificaciones',
  },
  {
    path: 'clientList',
    loadComponent: () =>
      import('./components/clients/client-list/client-list.component').then(
        (m) => m.ClientListComponent
      ),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:clients'],
      breadcrumb: { level: 1, description: 'Listado de clientes' },
      group: 'client',
      menu: { description: 'Listado de clientes' },
    },
    title: 'Listado de clientes',
  },
  {
    path: 'clientFaqs',
    component: FAQsComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      serviceType: 'Faqs',
      permissions: ['activate:faq'],
      breadcrumb: { level: 1, description: 'FAQs' },
      group: 'client',
      menu: { description: 'FAQs' },
    },
    title: 'FAQs de clientes',
  },
  {
    path: 'clients',
    loadChildren: () =>
      import('./components/clients/client-routes.routes').then(
        (r) => r.clientRoutes
      ),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:clients'],
      breadcrumb: { level: 2, description: '' },
      group: 'client',
      menu: { pathParent: 'clientList' },
      // Client pages read the client id from ?id= and can be opened directly (reload, link, Back).
      deepLink: true,
    },
  },
  {
    path: 'supplierList',
    component: SupplierListComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:contractor'],
      breadcrumb: { level: 1, description: 'Listado de prestadores' },
      group: 'contractor',
      menu: { description: 'Listado de prestadores' },
    },
    title: 'Listado de prestadores',
  },
  {
    path: 'supplierPayment',
    component: SupplierPaymentComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:contractorPayments'],
      breadcrumb: { level: 1, description: 'Pagos' },
      group: 'contractor',
      menu: { description: 'Pagos' },
    },
    title: 'Pagos',
  },
  {
    path: 'contractorFaqs',
    component: FAQsComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      serviceType: 'ContractorFaqs',
      permissions: ['add:contractorFaq'],
      breadcrumb: { level: 1, description: 'FAQs' },
      group: 'contractor',
      menu: { description: 'FAQs' },
    },
    title: 'FAQs de prestadores',
  },
  {
    path: 'supplier',
    component: SupplierProfileComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:contractor'],
      breadcrumb: { level: 2, description: 'Detalle de prestador' },
      group: 'contractor',
      menu: { pathParent: 'supplierList' },
      // The profile reads ?id= itself, so a reload or a shared link can open it directly.
      deepLink: true,
    },
    title: 'Perfil de prestador',
  },
  {
    path: 'scheduleTasks',
    component: ScheduleTasksComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:calendar'],
      breadcrumb: { level: 1, description: 'Calendario de Tareas' },
      group: 'tasks',
      menu: { description: 'Calendario de tareas' },
    },
    title: 'Calendario de Tareas',
  },
  {
    path: 'workload',
    component: WorkloadComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:workload'],
      breadcrumb: { level: 1, description: 'Asignar cliente a trabajo' },
      group: 'tasks',
      menu: { description: 'Orden de trabajo' },
    },
    title: 'Orden de trabajo',
  },
  {
    path: 'ManufacturingOrder',
    component: ManufacturingCalendarComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:assemblyorder'],
      breadcrumb: { level: 1, description: 'Orden de montaje' },
      group: 'tasks',
      menu: { description: 'Orden de montaje' },
    },
    title: 'Orden de montaje',
  },
  {
    path: 'ScheduleTasksCRMList',
    component: ScheduleTasksCRMListComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:crmTasks'],
      breadcrumb: { level: 1, description: 'Tareas CRM' },
      group: 'tasks',
      menu: { description: 'Tareas CRM' },
    },
    title: 'Tareas CRM',
  },
  {
    path: 'score',
    component: ScoreComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:score'],
      breadcrumb: { level: 1, description: 'Score' },
      group: 'scores',
      menu: { description: 'Score' },
    },
    title: 'Score',
  },
  {
    path: 'contractor-scores',
    loadChildren: () =>
      import('./components/score/score-standalone.routes').then(
        (c) => c.scoreRoutes
      ),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:contractorScore'],
      breadcrumb: { level: 2, description: '' },
      group: 'scores',
      menu: { pathParent: 'score' },
      deepLink: true, // supplier-score reads ?id= and reopens on reload
    },
  },
  {
    path: 'certifications',
    component: CertificationsComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:certifications'],
      breadcrumb: { level: 1, description: 'certifications' },
      group: 'scores',
      menu: { description: 'Certificaciones' },
    },
    title: 'Certificaciones',
  },
  {
    path: 'levels',
    component: LevelComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:levels'],
      breadcrumb: { level: 1, description: 'levels' },
      group: 'scores',
      menu: { description: 'Niveles' },
    },
    title: 'Niveles',
  },
  {
    path: 'factors',
    component: FactorComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:factors'],
      breadcrumb: { level: 1, description: 'factors' },
      group: 'scores',
      menu: { description: 'Factores' },
    },
    title: 'Factores',
  },
  {
    path: 'levelBenefits',
    component: LevelBenefitsComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:levels'],
      breadcrumb: { level: 1, description: 'levelBenefits' },
      group: 'scores',
      menu: { description: 'Beneficios' },
    },
    title: 'Beneficios por nivel',
  },
  {
    path: 'productList',
    component: ProductListComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:products'],
      breadcrumb: { level: 1, description: 'Productos' },
      group: 'setting',
      menu: { description: 'Productos' },
    },
    title: 'Productos',
  },
  {
    path: 'userManagement',
    loadComponent: () =>
      import('./components/user/user-list/user-list.component').then(
        (c) => c.UserListComponent
      ),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:users'],
      breadcrumb: { level: 1, description: 'Usuarios' },
      group: 'setting',
      menu: { description: 'Usuarios' },
    },
    title: 'Usuarios',
  },
  {
    path: 'distributionCenterList',
    loadComponent: () =>
      import(
        './components/distribution-center/distribution-center-list/distribution-center-list.component'
      ).then((c) => c.DistributionCenterListComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:distributioncenter'],
      breadcrumb: { level: 1, description: 'Centros de distribución' },
      group: 'setting',
      menu: { description: 'Centros de distribución' },
    },
    title: 'Centros de distribución',
  },
  {
    path: 'distributionCenter',
    loadChildren: () =>
      import(
        './components/distribution-center/distribution-center.routes'
      ).then((c) => c.DistributionCenterRoutes),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:distributioncenter'],
      breadcrumb: { level: 2, description: 'Centro de distribución' },
      group: 'setting',
      menu: { pathParent: 'distributionCenterList' },
    },
  },
  {
    path: 'tasksWithSubtasksList',
    loadComponent: () =>
      import(
        './components/tasks-subtasks/tasks-subtasks-list/tasks-subtasks-list.component'
      ).then((m) => m.TasksSubtasksListComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:taskgroup'],
      breadcrumb: { level: 1, description: 'Listado de tareas' },
      group: 'setting',
      menu: { description: 'ABM - Tareas y subtareas' },
    },
    title: 'Listado de tareas',
  },
  {
    path: 'taskRegistry',
    loadComponent: () =>
      import(
        './components/tasks-subtasks/tasks-subtasks-detail/tasks-subtasks-detail.component'
      ).then((m) => m.TasksSubtasksDetailComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:taskgroup'],
      breadcrumb: { level: 2, description: 'Registro de tareas' },
      group: 'setting',
      menu: { pathParent: 'tasksWithSubtasksList' },
    },
    title: 'Registro de tareas',
  },
  {
    path: 'salesPointsManagement',
    loadComponent: () =>
      import('./components/sales-points/sales-points.component').then(
        (c) => c.SalesPointsComponent
      ),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:pointofSaleAssignment'],
      breadcrumb: { level: 1, description: 'Puntos de venta' },
      group: 'setting',
      menu: { description: 'Puntos de venta' },
    },
    title: 'Puntos de venta',
  },
  {
    path: 'franchise-file-uploader',
    component: FranchiseFilesUploaderComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:pointofSaleAssignment'],
      breadcrumb: { level: 1, description: 'upload clients and suppliers' },
      group: 'setting',
      menu: { description: 'Carga de franquicias' },
    },
    title: 'Carga de franquicias',
  },
  {
    path: 'jsonManufacturingOrder',
    loadComponent: () =>
      import(
        './components/json-manufacturing-order/json-manufacturing-order.component'
      ).then((c) => c.JsonManufacturingOrderComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['add:aojson'],
      breadcrumb: { level: 1, description: 'JSON OA' },
      group: 'setting',
      menu: { description: 'JSON OA' },
    },
    title: 'JSON OA',
  },
  {
    path: 'SapArticles',
    component: SapArticlesComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:sapArticles'],
      breadcrumb: { level: 1, description: 'Artículos SAP' },
      group: 'marketing',
      menu: { description: 'SAP - Artículos' },
    },
    title: 'Artículos SAP',
  },
  {
    path: 'sapItemCategoriesList',
    loadComponent: () =>
      import(
        './components/sap-articles/sap-articles-categories-list/sap-articles-categories-list.component'
      ).then((m) => m.SapArticlesCategoriesListComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:sapArticles'],
      breadcrumb: { level: 1, description: 'Categorías SAP' },
      group: 'marketing',
      menu: { description: 'SAP - Categorías' },
    },
    title: 'Categorías SAP',
  },
  {
    path: 'itemCategoryABM',
    loadComponent: () =>
      import(
        './components/sap-articles/item-category-detail/item-category-detail.component'
      ).then((m) => m.ItemCategoryDetailComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:sapArticles'],
      breadcrumb: { level: 2, description: 'Categorías SAP' },
      group: 'marketing',
      menu: { pathParent: 'sapItemCategoriesList' },
    },
    title: 'Categoría SAP',
  },
  {
    path: 'franchises',
    component: FranchiseComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:franchise'],
      breadcrumb: { level: 1, description: 'Franchises' },
      group: 'franchise',
      menu: { description: 'Franquicias' },
    },
    title: 'Franquicias',
  },
  {
    path: 'franchis-point-sales',
    component: PointSaleListComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:franchise'],
      breadcrumb: { level: 2, description: 'Puntos de venta' },
      group: 'franchise',
      menu: { pathParent: 'franchises' },
    },
    title: 'Puntos de venta',
  },
  {
    path: 'franchis-zone',
    component: FranchiseZoneComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:franchise'],
      breadcrumb: { level: 2, description: 'Zona de franquicia' },
      group: 'franchise',
      menu: { pathParent: 'franchises' },
    },
    title: 'Zona de franquicia',
  },
  {
    path: 'services',
    component: ServiceListComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:services'],
      breadcrumb: { level: 1, description: 'Servicios' },
      group: 'marketing',
      menu: { description: 'Servicios' },
    },
    title: 'Servicios',
  },
  {
    path: 'advertisements',
    component: AdvertisementComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:ads'],
      breadcrumb: { level: 1, description: 'Publicidades' },
      group: 'marketing',
      menu: { description: 'Publicidades' },
    },
    title: 'Publicidades',
  },
  {
    path: 'serviceOffers',
    loadComponent: () =>
      import('./components/service-offer/service-offer-list/service-offer-list.component').then((m) => m.ServiceOfferListComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:campaigns'],
      breadcrumb: { level: 1, description: 'Campañas' },
      group: 'marketing',
      menu: { description: 'Campañas' },
    },
    title: 'Campañas',
  },
  {
    path: 'campaigns',
    loadChildren: () =>
      import('./components/service-offer/service-offer.routes'
      ).then((m) => m.serviceOfferRoutes),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:campaigns'],
      breadcrumb: { level: 2, description: '' },
      group: 'marketing',
      menu: { pathParent: 'serviceOffers' },
    },
  },
  {
    path: 'abmDynamicReports',
    loadComponent: () =>
      import(
        './components/dynamic-reports/dynamic-reports-list/dynamic-reports-list.component'
      ).then((m) => m.DynamicReportsListComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:reports'],
      breadcrumb: { level: 1, description: 'Reportes dinámicos' },
      group: 'reports',
      menu: { description: 'Reportes dinámicos' },
    },
    title: 'Listado de reportes dinámicos',
  },
  {
    path: 'dynamicReportsDetails',
    loadComponent: () =>
      import(
        './components/dynamic-reports/dynamic-reports-details/dynamic-reports-details.component'
      ).then((m) => m.DynamicReportsDetailsComponent),
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:dynamicreports'],
      breadcrumb: { level: 2, description: 'Detalle de reporte dinámico' },
      group: 'reports',
      menu: { pathParent: 'abmDynamicReports' },
    },
    title: 'Detalle de reporte dinámico',
  },
  {
    path: 'reports',
    component: ReportingTabsComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:reports'],
      breadcrumb: { level: 1, description: 'Reportes' },
      group: 'reports',
      menu: { description: 'Reportes' },
    },
    title: 'Reportes',
  },
  {
    path: 'budget-list',
    component: BudgetListComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:budgetList'],
      breadcrumb: { level: 1, description: 'Listado de Cotizaciones' },
      group: 'budgets',
      menu: { description: 'Listado de Cotizaciones' },
    },
    title: 'Listado de Cotizaciones',
  },
  {
    path: 'interest-rate',
    component: InterestRateComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['edit:funding'],
      breadcrumb: { level: 1, description: 'Planes de pago' },
      group: 'budgets',
      menu: { description: 'Planes de pago' },
    },
    title: 'Planes de pago',
  },
  {
    path: 'budget',
    component: BudgetComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['read:budget'],
      breadcrumb: { level: 2, description: 'Presupuesto' },
      group: 'budgets',
      menu: { pathParent: 'budget-list' },
    },
    title: 'Presupuesto',
  },
  {
    path: 'optional-accessory',
    component: OptionalAccessoryComponent,
    canActivate: [AuthenticationGuard, PermissionGuard],
    data: {
      permissions: ['edit:optionalAccessories'],
      breadcrumb: { level: 1, description: 'Accesorios opcionales' },
      group: 'budgets',
      menu: { description: 'Accesorios opcionales' },
    },
    title: 'Accesorios opcionales',
  },
  {
    path: '**',
    component: PageNotFoundComponent,
    title: 'Página no encontrada',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled',
      // A navigation cancelled by a guard ("¿Salir sin guardar?" -> Seguir editando) after the browser's Back puts the
      // history back where it was, so the next Back still works as expected.
      canceledNavigationResolution: 'computed',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
